import {
  Article,
  ArticleAdditionalFeedbackTypeNegative,
} from '@wix/answers-api';
import { Box, IconButton, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';

import { ChatMessage } from './ChatMessage';
import { UnhelpfulFeedbackOptions } from './UnhelpfulFeedbackOptions';
import { CHATBOT_REFERRALS } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import useIsArticlePageRedesignUnifiedUXEnabled from '../../../hooks/useIsArticlePageRedesignUnifiedUXEnabled';
import { ChevronLeftBoldIcon } from '../../../icons/ChevronLeftBoldIcon';
import { useArticlePageBi } from '../../../utils/article-page-bi';
import { useStore } from '../../../zustand/stores';
import {
  ChatbotFlow,
  CopilotDisplayMode,
} from '../../../zustand/stores/copilot';
import { PinnedChatInput } from '../../ArticlePageLayout/ArticleMainContent/PinnedChatInput';
import { CopilotMessageInput } from '../CopilotMessageInput';

import css from './index.module.scss';

type UnhelpfulFeedbackChatProps = {
  article: Article;
  sendMessage: (message: string, forceReferral?: string) => void;
  onCollapse?: () => void;
};

export const UnhelpfulFeedbackChat = ({
  article,
  sendMessage,
  onCollapse,
}: UnhelpfulFeedbackChatProps) => {
  const isCopilotRedesign = useIsArticlePageRedesignUnifiedUXEnabled();
  const {
    setCopilotDisplayMode,
    copilotDisplayMode,
    setChatbotReferral,
    chatbotFlow,
  } = useStore((state) => state.copilotSlice);
  const [showBorderAnimation, setShowBorderAnimation] = useState(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const [unhelpfulFeedbackOptionSelected, setUnhelpfulFeedbackOptionSelected] =
    useState<ArticleAdditionalFeedbackTypeNegative | null>(null);
  const { unhelpfulFeedbackTrack, unhelpfulFreeTextPageClick } =
    useArticlePageBi(article);

  const isOtherOptionSelected =
    unhelpfulFeedbackOptionSelected ===
    ArticleAdditionalFeedbackTypeNegative.OTHER;

  const handleFreeTextSubmit = async (text: string) => {
    await unhelpfulFeedbackTrack(text, '', '');
    await unhelpfulFreeTextPageClick(text);
    setChatbotReferral(CHATBOT_REFERRALS.UNHELPFUL_ARTICLE);
  };

  useEffect(() => {
    if (copilotDisplayMode === CopilotDisplayMode.UnhelpfulFeedback) {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      setShowBorderAnimation(true);

      animationTimeoutRef.current = setTimeout(() => {
        setShowBorderAnimation(false);
        animationTimeoutRef.current = null;
      }, 5000);
    }
  }, [copilotDisplayMode]);

  const isContactFlow = chatbotFlow === ChatbotFlow.CONTACT;

  return (
    <div
      className={classNames({
        [css.showBorderAnimation]: showBorderAnimation,
        [css.copilotRedesign]: isCopilotRedesign,
      })}
      style={
        isCopilotRedesign
          ? { position: 'absolute', height: '100%', width: '100%', border: '1px solid transparent', boxSizing: 'border-box' }
          : { position: 'absolute', height: '100%', border: '1px solid transparent', boxSizing: 'border-box' }
      }
    >
      <Box
        className={classNames(css.wrapper, {
          [css.otherOptionSelected]: isOtherOptionSelected && !isContactFlow,
          [css.copilotRedesign]: isCopilotRedesign,
        })}
        direction="vertical"
        gap="3"
      >
        {isCopilotRedesign && onCollapse && (
          <div className={css.redesignHeader}>
            <IconButton
              size="small"
              skin="dark"
              priority="secondary"
              onClick={onCollapse}
            >
              <span>
                <Image
                  src="https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/article-page/box.svg"
                  alt="collapse"
                  width={18}
                  height={18}
                />
              </span>
            </IconButton>
          </div>
        )}
        {!isCopilotRedesign && (
          <Box className={css.titleWrapper} align="space-between">
            <Box>
              <div
                onClick={() =>
                  setCopilotDisplayMode(CopilotDisplayMode.CopilotMainView)
                }
                className={css.backButton}
                data-hook={DATA_HOOKS.UNHELPFUL_FEEDBACK_BACK_BUTTON}
              >
                <ChevronLeftBoldIcon />
              </div>
              <Text size="medium" weight="bold">
                {t('article-page.copilot.chatbot')}
              </Text>
            </Box>
          </Box>
        )}
        <Box
          direction="vertical"
          gap={2}
          padding={undefined}
        >
          <ChatMessage
            message={t('article-page.copilot.unhelpful-feedback.message')}
          />
          <UnhelpfulFeedbackOptions
            unhelpfulFeedbackOptionSelected={unhelpfulFeedbackOptionSelected}
            setUnhelpfulFeedbackOptionSelected={
              setUnhelpfulFeedbackOptionSelected
            }
            article={article}
            sendMessage={sendMessage}
          />
          {(isOtherOptionSelected ||
            (isContactFlow && !!unhelpfulFeedbackOptionSelected)) && (
            <ChatMessage
              message={t(
                isContactFlow
                  ? 'article-page.copilot.unhelpful-feedback.message-contact-flow'
                  : 'article-page.copilot.unhelpful-feedback.other-message'
              )}
            />
          )}
        </Box>
        {isOtherOptionSelected && !isContactFlow && (
          <Box
            padding={!isCopilotRedesign ? '0 SP1' : '0'}
            width="100%"
            direction="vertical"
            boxSizing="border-box"
          >
            {isCopilotRedesign ? (
              <PinnedChatInput
                article={article}
                alwaysFocused
                hideRelatedQuestions
                forceReferral={CHATBOT_REFERRALS.UNHELPFUL_ARTICLE}
                onBeforeSend={handleFreeTextSubmit}
              />
            ) : (
              <CopilotMessageInput
                relatedQuestions={[]}
                article={article}
                sendMessage={sendMessage}
                onTextSubmit={handleFreeTextSubmit}
              />
            )}
          </Box>
        )}
      </Box>
    </div>
  );
};
